export const OPERATOR_READINESS_SECTION_IDS = [
  'business_profile',
  'offerings',
  'service_area',
  'availability',
  'calendar_sync',
] as const

export type OperatorReadinessSectionId = (typeof OPERATOR_READINESS_SECTION_IDS)[number]

export const OPERATOR_READINESS_SURFACES = [
  'business',
  'services',
  'service-area',
  'availability',
  'integrations',
] as const

export type OperatorReadinessSurface = (typeof OPERATOR_READINESS_SURFACES)[number]

export type OperatorReadinessSeverity = 'blocking' | 'recommended'
export type OperatorReadinessSummaryStatus = 'ready' | 'blocked'
export type OperatorReadinessSurfaceStatus = 'complete' | 'action_required'

export interface OperatorReadinessInput {
  operator: {
    businessName: string | null
    slug: string | null
    phone: string | null
  } | null
  services: Array<{
    isActive: boolean
    durationMinutes: number
    price: number | null
  }>
  serviceArea: {
    centerLat: string | null
    centerLng: string | null
    softRadiusMiles: number
    hardRadiusMiles: number
  } | null
  availabilitySlots: Array<{ id: string }>
  calendarSyncRequired?: boolean
  calendarConnections?: Array<{
    status: string
    syncError?: string | null
    sources: Array<{
      importEnabled: boolean
      writeDestination: boolean
    }>
  }>
}

export interface OperatorReadinessOwner {
  href: string
  label: string
  surface: OperatorReadinessSurface
}

export interface OperatorReadinessItem {
  id: string
  title: string
  detail: string
  severity: OperatorReadinessSeverity
  owner: OperatorReadinessOwner
}

export interface OperatorReadinessSection {
  id: OperatorReadinessSectionId
  title: string
  owner: OperatorReadinessOwner
  complete: boolean
  blockingItems: OperatorReadinessItem[]
  recommendedItems: OperatorReadinessItem[]
}

export interface OperatorReadiness {
  isLaunchReady: boolean
  blockingItemCount: number
  recommendedImprovementCount: number
  sections: OperatorReadinessSection[]
}

export interface OperatorReadinessSummary {
  launchStatus: OperatorReadinessSummaryStatus
  launchStatusLabel: string
  blockingItemCount: number
  recommendedImprovementCount: number
  currentSurfaceStatus: OperatorReadinessSurfaceStatus | null
  reviewHref: string
}

const READINESS_OWNERS: Record<OperatorReadinessSectionId, OperatorReadinessOwner> = {
  business_profile: {
    href: '/business',
    label: 'Business profile',
    surface: 'business',
  },
  offerings: {
    href: '/services',
    label: 'Services',
    surface: 'services',
  },
  service_area: {
    href: '/service-area',
    label: 'Service area',
    surface: 'service-area',
  },
  availability: {
    href: '/availability',
    label: 'Availability',
    surface: 'availability',
  },
  calendar_sync: {
    href: '/integrations',
    label: 'Calendar sync',
    surface: 'integrations',
  },
}

function createItem({
  id,
  title,
  detail,
  severity,
  sectionId,
}: {
  id: string
  title: string
  detail: string
  severity: OperatorReadinessSeverity
  sectionId: OperatorReadinessSectionId
}): OperatorReadinessItem {
  return {
    id,
    title,
    detail,
    severity,
    owner: READINESS_OWNERS[sectionId],
  }
}

function createSection({
  id,
  title,
  blockingItems,
  recommendedItems = [],
}: {
  id: OperatorReadinessSectionId
  title: string
  blockingItems: OperatorReadinessItem[]
  recommendedItems?: OperatorReadinessItem[]
}): OperatorReadinessSection {
  return {
    id,
    title,
    owner: READINESS_OWNERS[id],
    complete: blockingItems.length === 0 && recommendedItems.length === 0,
    blockingItems,
    recommendedItems,
  }
}

export function evaluateOperatorReadiness(input: OperatorReadinessInput): OperatorReadiness {
  const activeServices = input.services.filter((service) => service.isActive)
  const servicesWithPricing = activeServices.filter(
    (service) => service.durationMinutes >= 5 && service.price != null
  )
  const hasServiceArea = Boolean(
    input.serviceArea?.centerLat &&
      input.serviceArea.centerLng &&
      input.serviceArea.softRadiusMiles >= 1 &&
      input.serviceArea.hardRadiusMiles >= input.serviceArea.softRadiusMiles
  )

  const businessBlockingItems: OperatorReadinessItem[] = []
  const businessRecommendedItems: OperatorReadinessItem[] = []

  if (!input.operator) {
    businessBlockingItems.push(
      createItem({
        id: 'operator-profile-missing',
        title: 'Add your business profile',
        detail: 'Business name and booking slug are required before customers can book.',
        severity: 'blocking',
        sectionId: 'business_profile',
      })
    )
  } else {
    if (!input.operator.businessName || !input.operator.slug) {
      businessBlockingItems.push(
        createItem({
          id: 'operator-basics-incomplete',
          title: 'Complete business details',
          detail: 'Business name and booking slug are both required.',
          severity: 'blocking',
          sectionId: 'business_profile',
        })
      )
    }

    if (!input.operator.phone) {
      businessRecommendedItems.push(
        createItem({
          id: 'operator-phone-missing',
          title: 'Add a phone number',
          detail: 'A phone number improves trust on the booking page.',
          severity: 'recommended',
          sectionId: 'business_profile',
        })
      )
    }
  }

  const offeringsBlockingItems: OperatorReadinessItem[] = []
  if (activeServices.length === 0) {
    offeringsBlockingItems.push(
      createItem({
        id: 'active-offering-missing',
        title: 'Add at least one service',
        detail: 'Your booking page needs one active service before it can accept appointments.',
        severity: 'blocking',
        sectionId: 'offerings',
      })
    )
  }
  if (servicesWithPricing.length === 0) {
    offeringsBlockingItems.push(
      createItem({
        id: 'offering-pricing-missing',
        title: 'Add duration and price',
        detail: 'At least one active service needs a valid duration and price.',
        severity: 'blocking',
        sectionId: 'offerings',
      })
    )
  }

  const serviceAreaBlockingItems: OperatorReadinessItem[] = []
  if (!hasServiceArea) {
    serviceAreaBlockingItems.push(
      createItem({
        id: 'service-area-missing',
        title: 'Set your service area',
        detail: 'Choose where you start from and how far customers can book.',
        severity: 'blocking',
        sectionId: 'service_area',
      })
    )
  }

  const availabilityBlockingItems: OperatorReadinessItem[] = []
  if (input.availabilitySlots.length === 0) {
    availabilityBlockingItems.push(
      createItem({
        id: 'availability-missing',
        title: 'Add weekly hours',
        detail: 'Customers cannot see times until at least one weekly window exists.',
        severity: 'blocking',
        sectionId: 'availability',
      })
    )
  }

  const calendarConnections = input.calendarConnections ?? []
  const activeCalendarConnections = calendarConnections.filter(
    (connection) => connection.status === 'active'
  )
  const hasImportedCalendar = activeCalendarConnections.some((connection) =>
    connection.sources.some((source) => source.importEnabled)
  )
  const hasWriteDestination = activeCalendarConnections.some((connection) =>
    connection.sources.some((source) => source.writeDestination)
  )
  const hasCalendarConnectivityIssue = calendarConnections.some(
    (connection) => connection.status === 'needs_reauth' || Boolean(connection.syncError)
  )
  const calendarBlockingItems: OperatorReadinessItem[] = []
  const calendarRecommendedItems: OperatorReadinessItem[] = []

  if (input.calendarSyncRequired !== false && !hasImportedCalendar) {
    calendarBlockingItems.push(
      createItem({
        id: 'calendar-sync-missing',
        title: 'Connect Google or Outlook Calendar',
        detail: 'Launch readiness requires at least one active imported calendar.',
        severity: 'blocking',
        sectionId: 'calendar_sync',
      })
    )
  }

  if (input.calendarSyncRequired !== false && hasCalendarConnectivityIssue) {
    calendarBlockingItems.push(
      createItem({
        id: 'calendar-sync-connectivity-issue',
        title: 'Resolve calendar connectivity',
        detail:
          'Zippity keeps using the last imported events, but the operator must fix sync errors before launch.',
        severity: 'blocking',
        sectionId: 'calendar_sync',
      })
    )
  }

  if (hasImportedCalendar && !hasWriteDestination) {
    calendarRecommendedItems.push(
      createItem({
        id: 'calendar-writeback-missing',
        title: 'Choose a writeback calendar',
        detail:
          'Appointment writeback is enabled by default and works when one calendar is selected.',
        severity: 'recommended',
        sectionId: 'calendar_sync',
      })
    )
  }

  const sections = [
    createSection({
      id: 'business_profile',
      title: 'Business profile',
      blockingItems: businessBlockingItems,
      recommendedItems: businessRecommendedItems,
    }),
    createSection({
      id: 'offerings',
      title: 'Services',
      blockingItems: offeringsBlockingItems,
    }),
    createSection({
      id: 'service_area',
      title: 'Service area',
      blockingItems: serviceAreaBlockingItems,
    }),
    createSection({
      id: 'availability',
      title: 'Availability',
      blockingItems: availabilityBlockingItems,
    }),
    createSection({
      id: 'calendar_sync',
      title: 'Calendar sync',
      blockingItems: calendarBlockingItems,
      recommendedItems: calendarRecommendedItems,
    }),
  ] satisfies OperatorReadinessSection[]

  const blockingItemCount = sections.reduce(
    (count, section) => count + section.blockingItems.length,
    0
  )
  const recommendedImprovementCount = sections.reduce(
    (count, section) => count + section.recommendedItems.length,
    0
  )

  return {
    isLaunchReady: blockingItemCount === 0,
    blockingItemCount,
    recommendedImprovementCount,
    sections,
  }
}

export function getOperatorReadinessSummary({
  readiness,
  currentSurface,
}: {
  readiness: OperatorReadiness
  currentSurface?: OperatorReadinessSurface
}): OperatorReadinessSummary {
  const currentSection = currentSurface
    ? (readiness.sections.find((section) => section.owner.surface === currentSurface) ?? null)
    : null

  return {
    launchStatus: readiness.isLaunchReady ? 'ready' : 'blocked',
    launchStatusLabel: readiness.isLaunchReady ? 'Ready to launch' : 'Needs attention',
    blockingItemCount: readiness.blockingItemCount,
    recommendedImprovementCount: readiness.recommendedImprovementCount,
    currentSurfaceStatus: currentSection
      ? currentSection.complete
        ? 'complete'
        : 'action_required'
      : null,
    reviewHref: '/setup',
  }
}
